/**
 * useWeeklyVolume — Rolling 7-Day Hard-Set Volume Hook
 *
 * Implements the weekly volume tracking model:
 *   V_weekly = Σ Sets_i  (where RPE ≥ 7, i.e. RIR ≤ 3)
 *
 * Synergistic allocation:
 *   Primary muscle   → 1.0 set contribution
 *   Secondary muscle → 0.5 set contribution
 *
 * Per-muscle targets are derived from the user's experience level and
 * training goal — major movers (chest, lats, quads) get full targets,
 * moderate muscles (delts, biceps, triceps) get 80%, and small accessory
 * muscles (forearms, calves, core) get 60%.
 *
 * Session cap: 8 hard sets per muscle per session
 *
 * The rolling window is computed live from loggedSets timestamps, so it
 * stays accurate even if the app is closed and reopened days later.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import type { ExperienceLevel, MuscleGroup, TrainingGoal } from '@/types';

// ── Constants ────────────────────────────────────────────────

export const WEEKLY_VOLUME_MIN = 10;   // MEV — Minimum Effective Volume
export const WEEKLY_VOLUME_TARGET = 15; // Legacy fallback — use getMuscleWeeklyTarget() instead
export const WEEKLY_VOLUME_MAX = 20;   // MRV — Maximum Recoverable Volume
export const SESSION_VOLUME_CAP = 8;   // Max hard sets per muscle per session

const ROLLING_WINDOW_MS = 7 * 24 * 3_600_000; // 7 days in ms
const MIN_RPE_FOR_HARD_SET = 7;

// ── Per-Muscle Target System ──────────────────────────────────

/**
 * Muscle importance tier — scales the weekly target.
 * Major movers accumulate volume quickly (compounds hit them hard).
 * Minor accessory muscles need fewer dedicated sets to reach effective volume.
 */
const MUSCLE_TIER: Record<MuscleGroup, 'major' | 'moderate' | 'minor'> = {
  chest:        'major',
  lats:         'major',
  quads:        'major',
  hamstrings:   'major',
  glutes:       'major',
  front_delt:   'moderate',
  lateral_delt: 'moderate',
  rear_delt:    'moderate',
  biceps:       'moderate',
  triceps:      'moderate',
  traps:        'moderate',
  rhomboids:    'moderate',
  lower_back:   'moderate',
  forearms:     'minor',
  calves:       'minor',
  core:         'minor',
  hip_flexors:  'minor',
};

const TIER_MULTIPLIER = { major: 1.0, moderate: 0.8, minor: 0.6 };

/**
 * Base weekly set target (for major muscles) by experience × goal.
 * Strength focuses on lower volume/higher intensity; hypertrophy maximises volume.
 */
const BASE_TARGET: Record<ExperienceLevel, Record<TrainingGoal, number>> = {
  beginner:     { strength: 8,  hypertrophy: 10, general_fitness: 9  },
  intermediate: { strength: 10, hypertrophy: 14, general_fitness: 12 },
  advanced:     { strength: 13, hypertrophy: 18, general_fitness: 15 },
};

/**
 * Returns the weekly hard-set target for a specific muscle, adjusted for the
 * user's experience level, training goal, and muscle importance tier.
 *
 * Examples (intermediate, hypertrophy):
 *   chest → 14   (major × 1.0)
 *   biceps → 11  (moderate × 0.8)
 *   calves → 8   (minor × 0.6)
 */
export function getMuscleWeeklyTarget(
  muscle: MuscleGroup,
  experienceLevel: ExperienceLevel = 'intermediate',
  trainingGoal: TrainingGoal = 'hypertrophy',
): number {
  const base = BASE_TARGET[experienceLevel]?.[trainingGoal] ?? 14;
  const multiplier = TIER_MULTIPLIER[MUSCLE_TIER[muscle]];
  return Math.max(1, Math.round(base * multiplier));
}

/** Convenience: returns a full MuscleGroup → target map given user profile settings. */
export function buildMuscleTargetMap(
  experienceLevel: ExperienceLevel = 'intermediate',
  trainingGoal: TrainingGoal = 'hypertrophy',
): Map<MuscleGroup, number> {
  const map = new Map<MuscleGroup, number>();
  for (const muscle of Object.keys(MUSCLE_TIER) as MuscleGroup[]) {
    map.set(muscle, getMuscleWeeklyTarget(muscle, experienceLevel, trainingGoal));
  }
  return map;
}

// ── Pure helpers ─────────────────────────────────────────────

/** Returns the cutoff ISO string for the rolling 7-day window */
export function rollingWindowStart(): string {
  return new Date(Date.now() - ROLLING_WINDOW_MS).toISOString();
}

/**
 * Determines if a set qualifies as a "hard set" for volume accounting.
 * Hard set: RPE ≥ 7 (RIR ≤ 3) and not a warm-up set.
 */
export function isHardSet(rpe: number, isWarmup: boolean): boolean {
  return !isWarmup && rpe >= MIN_RPE_FOR_HARD_SET;
}

// ── Reactive Hooks ────────────────────────────────────────────

/**
 * Live rolling 7-day hard-set count for a single muscle.
 * Counts fractional contributions from synergistic exercises.
 * Target is derived from the user's profile stored in AppSettings.
 */
export function useMuscleWeeklyVolume(muscle: MuscleGroup): {
  hardSets: number;
  targetSets: number;
  percentOfTarget: number;
  isAtMEV: boolean;
  isAtTarget: boolean;
  isAtMRV: boolean;
} {
  const settings = useLiveQuery(() => db.settings.get('singleton'), []);

  const result = useLiveQuery(async () => {
    const cutoff = rollingWindowStart();

    const recentSets = await db.loggedSets
      .where('loggedAt')
      .aboveOrEqual(cutoff)
      .filter((s) => isHardSet(s.rpe, s.isWarmup))
      .toArray();

    if (recentSets.length === 0) return 0;

    const exerciseIds = [...new Set(recentSets.map((s) => s.exerciseId))];
    const exercises = await db.exercises.bulkGet(exerciseIds);
    const exerciseMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));

    let total = 0;
    for (const set of recentSets) {
      const ex = exerciseMap.get(set.exerciseId);
      if (!ex) continue;
      const allocation = ex.muscles.find((m) => m.muscle === muscle);
      if (allocation) {
        total += allocation.fraction;
      }
    }

    return Math.round(total * 10) / 10;
  }, [muscle]);

  const hardSets = result ?? 0;
  const targetSets = getMuscleWeeklyTarget(
    muscle,
    settings?.experienceLevel,
    settings?.trainingGoal,
  );
  const mrv = Math.round(targetSets * (WEEKLY_VOLUME_MAX / WEEKLY_VOLUME_TARGET));

  return {
    hardSets,
    targetSets,
    percentOfTarget: Math.min(Math.round((hardSets / targetSets) * 100), 100),
    isAtMEV: hardSets >= WEEKLY_VOLUME_MIN,
    isAtTarget: hardSets >= targetSets,
    isAtMRV: hardSets >= mrv,
  };
}

/**
 * Returns a volume map for ALL muscles — used by the Dashboard and
 * the workout generator to determine volume needs.
 */
export function useAllMuscleWeeklyVolume(): Map<MuscleGroup, number> {
  const result = useLiveQuery(async () => {
    const cutoff = rollingWindowStart();

    const recentSets = await db.loggedSets
      .where('loggedAt')
      .aboveOrEqual(cutoff)
      .filter((s) => isHardSet(s.rpe, s.isWarmup))
      .toArray();

    const exerciseIds = [...new Set(recentSets.map((s) => s.exerciseId))];
    const exercises = await db.exercises.bulkGet(exerciseIds);
    const exerciseMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));

    const volumeMap = new Map<MuscleGroup, number>();

    for (const set of recentSets) {
      const ex = exerciseMap.get(set.exerciseId);
      if (!ex) continue;
      for (const allocation of ex.muscles) {
        const current = volumeMap.get(allocation.muscle) ?? 0;
        volumeMap.set(allocation.muscle, current + allocation.fraction);
      }
    }

    return volumeMap;
  }, []);

  return result ?? new Map();
}

/**
 * Returns the number of hard sets already logged for a muscle in the
 * CURRENT session — used by the generator to enforce the session cap.
 */
export function useSessionVolume(workoutId: string): Map<MuscleGroup, number> {
  const result = useLiveQuery(async () => {
    if (!workoutId) return new Map<MuscleGroup, number>();

    const sets = await db.loggedSets
      .where('workoutId')
      .equals(workoutId)
      .filter((s) => isHardSet(s.rpe, s.isWarmup))
      .toArray();

    const exerciseIds = [...new Set(sets.map((s) => s.exerciseId))];
    const exercises = await db.exercises.bulkGet(exerciseIds);
    const exerciseMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));

    const sessionMap = new Map<MuscleGroup, number>();
    for (const set of sets) {
      const ex = exerciseMap.get(set.exerciseId);
      if (!ex) continue;
      for (const allocation of ex.muscles) {
        const current = sessionMap.get(allocation.muscle) ?? 0;
        sessionMap.set(allocation.muscle, current + allocation.fraction);
      }
    }

    return sessionMap;
  }, [workoutId]);

  return result ?? new Map();
}

/**
 * Remaining sets needed to hit the per-muscle target for each muscle.
 * Targets are derived from the user's experience level and training goal.
 * Negative values mean the muscle has exceeded the target.
 */
export function useVolumeDeficit(): Map<MuscleGroup, number> {
  const volumeMap = useAllMuscleWeeklyVolume();
  const settings = useLiveQuery(() => db.settings.get('singleton'), []);

  const ALL_MUSCLES: MuscleGroup[] = [
    'chest', 'front_delt', 'lateral_delt', 'rear_delt',
    'triceps', 'biceps', 'forearms',
    'lats', 'traps', 'rhomboids', 'lower_back',
    'quads', 'hamstrings', 'glutes', 'calves',
    'core', 'hip_flexors',
  ];

  const deficitMap = new Map<MuscleGroup, number>();
  for (const muscle of ALL_MUSCLES) {
    const done = volumeMap.get(muscle) ?? 0;
    const target = getMuscleWeeklyTarget(
      muscle,
      settings?.experienceLevel,
      settings?.trainingGoal,
    );
    deficitMap.set(muscle, Math.max(0, target - done));
  }
  return deficitMap;
}
